import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import useLang from "../hooks/useLang";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Brain, PaperPlaneTilt } from "@phosphor-icons/react";
import CloneAvatar from "../components/CloneAvatar";
import { toast } from "sonner";

export default function PublicClone() {
  const { id } = useParams();
  const { t } = useLang();
  const [clone, setClone] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const consentKey = `consent_public_${id}`;
  const [consented, setConsented] = useState(() => localStorage.getItem(consentKey) === "1");
  const [showConsent, setShowConsent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Remove auth header for public access
        const [c, s] = await Promise.all([
          api.get(`/clones/${id}`),
          api.get(`/clones/${id}/stats`),
        ]);
        setClone(c.data);
        setStats(s.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Clone non accessibile");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    if (!consented) {
      setShowConsent(true);
      return;
    }
    const q = input.trim();
    setInput("");
    const newMsgs = [...messages, { role: "user", text: q }];
    setMessages(newMsgs);
    setThinking(true);
    try {
      const { data } = await api.post(`/clones/${id}/chat`, { question: q });
      setMessages([...newMsgs, { role: "clone", text: data.response, role_name: data.role_name }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("errorGeneric"));
      setMessages(newMsgs);
    } finally {
      setThinking(false);
    }
  };

  if (loading) return <div className="p-12 text-zinc-500 font-mono text-sm">{t("loading")}</div>;
  if (!clone) return <div className="p-12 text-zinc-500">Clone non disponibile</div>;

  const grantConsent = () => {
    localStorage.setItem(consentKey, "1");
    setConsented(true);
    setShowConsent(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Dialog open={showConsent} onOpenChange={setShowConsent}>
        <DialogContent className="rounded-md border-zinc-200 max-w-md" data-testid="public-consent-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">{t("consentTitle")}</DialogTitle>
            <DialogDescription className="text-zinc-600 leading-relaxed pt-3">
              {t("consentBody")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setShowConsent(false)}>{t("consentDecline")}</Button>
            <Button onClick={grantConsent} data-testid="public-consent-accept" className="bg-zinc-950 hover:bg-zinc-800 rounded-md">
              {t("consentAccept")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-zinc-950 text-white flex items-center justify-center rounded-md">
              <Brain size={18} weight="duotone" />
            </div>
            <span className="font-heading text-lg font-medium tracking-tight">{t("appName")}</span>
          </Link>
          <LanguageSwitcher small />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <section className="lg:col-span-8">
            <div className="overline mb-3">/// Public · {t("respondingAs")} {clone.role_name}</div>
            <h1 className="font-heading text-4xl tracking-tight font-light mb-2">{clone.role_name}</h1>
            <p className="text-zinc-500 text-sm mb-8">{clone.industry} · {stats?.answer_count ?? 0} {t("answers")}</p>

            <div className="space-y-4 min-h-[300px] mb-6">
              {messages.length === 0 ? (
                <div className="border border-dashed border-zinc-300 rounded-md p-12 text-center">
                  <p className="text-zinc-500 text-sm">{t("askTheClone")}</p>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%]">
                      {m.role === "clone" && (
                        <div className="overline mb-1.5 text-zinc-400">
                          /// {t("respondingAs")} {m.role_name}
                        </div>
                      )}
                      <div
                        className={`px-5 py-4 rounded-md whitespace-pre-wrap text-[15px] leading-relaxed ${
                          m.role === "user"
                            ? "bg-zinc-950 text-white rounded-br-sm"
                            : "bg-white border border-zinc-200 text-zinc-900 rounded-bl-sm"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  </div>
                ))
              )}
              {thinking && (
                <div className="flex justify-start">
                  <div className="bg-white border border-zinc-200 px-5 py-4 rounded-md text-zinc-500 text-sm">
                    {t("thinking")}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 pt-6">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("askTheClone")}
                data-testid="public-chat-input"
                className="rounded-md border-zinc-200 h-12 text-base"
              />
              <Button
                type="submit"
                disabled={thinking || !input.trim()}
                data-testid="public-chat-send"
                className="h-12 px-5 bg-zinc-950 hover:bg-zinc-800 rounded-md"
              >
                <PaperPlaneTilt size={16} weight="fill" />
              </Button>
            </form>
          </section>

          <aside className="lg:col-span-4 lg:sticky lg:top-8 space-y-6">
            <div>
              <div className="mb-4">
                <CloneAvatar clone={clone} size={64} />
              </div>
              <div className="overline mb-2">/// {clone.industry}</div>
              <h2 className="font-heading text-xl font-medium tracking-tight mb-3">{clone.role_name}</h2>
              <p className="text-sm text-zinc-600 leading-relaxed">{clone.responsibilities}</p>
            </div>
            {stats?.topics?.length > 0 && (
              <div className="border-t border-zinc-200 pt-6">
                <div className="overline mb-3">/// {t("topicsCovered")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {stats.topics.slice(0, 20).map((tp) => (
                    <span key={tp.name} className="tag">
                      {tp.name} · {tp.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
